import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';

import { LookerReportType } from 'graphql-query-contracts';
import { getLookerEmbedURL } from 'lane-shared/graphql/looker';
import { Channel } from 'lane-shared/types/ChannelType';
import { LookerReportEnum } from 'constants-integrations';

import { REPORT_IDS } from '../constants';

class GetReportEmbedURLError extends Error {}

class InvalidReportError extends Error {}

const LOOKER_REPORT_MAPPING: Partial<
  Record<LookerReportEnum, LookerReportType>
> = {
  [LookerReportEnum.WorkOrdersServiceRequestsReporting]:
    LookerReportType.WorkOrdersServiceRequestsReporting,
  [LookerReportEnum.WorkOrdersTargetTimesReport]:
    LookerReportType.WorkOrdersTargetTimesReport,
  [LookerReportEnum.WorkOrdersPreventativeMaintenanceTasksReporting]:
    LookerReportType.WorkOrdersPreventativeMaintenanceTasksReporting,
  [LookerReportEnum.WorkOrdersServiceRequestResponseTimeAll]:
    LookerReportType.WorkOrdersServiceRequestResponseTimeAll,
  [LookerReportEnum.WorkOrdersServiceRequestResponseTimeAverage]:
    LookerReportType.WorkOrdersServiceRequestResponseTimeAverage,
  [LookerReportEnum.WorkOrdersServiceRequestCompletionTimeAll]:
    LookerReportType.WorkOrdersServiceRequestCompletionTimeAll,
  [LookerReportEnum.WorkOrdersServiceRequestCompletionTimeAverage]:
    LookerReportType.WorkOrdersServiceRequestCompletionTimeAverage,
  [LookerReportEnum.WorkOrdersServiceRequestAcceptanceTimeAll]:
    LookerReportType.WorkOrdersServiceRequestAcceptanceTimeAll,
  [LookerReportEnum.WorkOrdersServiceRequestAcceptanceTimeAverage]:
    LookerReportType.WorkOrdersServiceRequestAcceptanceTimeAverage,
  [LookerReportEnum.WorkOrdersServiceRequestTenantSurveySummaryAll]:
    LookerReportType.WorkOrdersServiceRequestTenantSurveySummaryAll,
  [LookerReportEnum.WorkOrdersServiceRequestTenantSurveySummaryDetails]:
    LookerReportType.WorkOrdersServiceRequestTenantSurveySummaryDetails,
  [LookerReportEnum.WorkOrdersPreventativeMaintenanceTimeEvaluationByTask]:
    LookerReportType.WorkOrdersPreventativeMaintenanceTimeEvaluationByTask,
  [LookerReportEnum.WorkOrdersPreventativeMaintenanceTaskPerformanceByProperty]:
    LookerReportType.WorkOrdersPreventativeMaintenanceTaskPerformanceByProperty,
  [LookerReportEnum.WorkOrdersPreventativeMaintenanceTaskPerformanceByAssignedUser]:
    LookerReportType.WorkOrdersPreventativeMaintenanceTaskPerformanceByAssignedUser,
  [LookerReportEnum.WorkOrdersPreventativeMaintenanceTaskPerformanceByAssignedTeam]:
    LookerReportType.WorkOrdersPreventativeMaintenanceTaskPerformanceByAssignedTeam,
  [LookerReportEnum.WorkOrdersPreventativeMaintenanceTaskPerformanceBySchedule]:
    LookerReportType.WorkOrdersPreventativeMaintenanceTaskPerformanceBySchedule,
};

const REPORT_ID_TO_ENUM_MAP = {
  [REPORT_IDS.SERVICE_REQUESTS_SUMMARY]:
    LookerReportEnum.WorkOrdersServiceRequestsReporting,
  [REPORT_IDS.TARGET_TIMES_REPORT]:
    LookerReportEnum.WorkOrdersTargetTimesReport,
  [REPORT_IDS.PREVENTIVE_MAINTENANCE_TASKS_SUMMARY]:
    LookerReportEnum.WorkOrdersPreventativeMaintenanceTasksReporting,
  [REPORT_IDS.SERVICE_REQUEST_RESPONSE_TIME_ALL]:
    LookerReportEnum.WorkOrdersServiceRequestResponseTimeAll,
  [REPORT_IDS.SERVICE_REQUEST_RESPONSE_TIME_AVERAGE]:
    LookerReportEnum.WorkOrdersServiceRequestResponseTimeAverage,
  [REPORT_IDS.SERVICE_REQUEST_COMPLETION_TIME_ALL]:
    LookerReportEnum.WorkOrdersServiceRequestCompletionTimeAll,
  [REPORT_IDS.SERVICE_REQUEST_COMPLETION_TIME_AVERAGE]:
    LookerReportEnum.WorkOrdersServiceRequestCompletionTimeAverage,
  [REPORT_IDS.SERVICE_REQUEST_ACCEPTANCE_TIME_ALL]:
    LookerReportEnum.WorkOrdersServiceRequestAcceptanceTimeAll,
  [REPORT_IDS.SERVICE_REQUEST_ACCEPTANCE_TIME_AVERAGE]:
    LookerReportEnum.WorkOrdersServiceRequestAcceptanceTimeAverage,
  [REPORT_IDS.PREVENTIVE_MAINTENANCE_TIME_EVALUATION_BY_TASK]:
    LookerReportEnum.WorkOrdersPreventativeMaintenanceTimeEvaluationByTask,
  [REPORT_IDS.PREVENTIVE_MAINTENANCE_TASK_PERFORMANCE_BY_PROPERTY]:
    LookerReportEnum.WorkOrdersPreventativeMaintenanceTaskPerformanceByProperty,
  [REPORT_IDS.PREVENTIVE_MAINTENANCE_TASK_PERFORMANCE_BY_ASSIGNED_USER]:
    LookerReportEnum.WorkOrdersPreventativeMaintenanceTaskPerformanceByAssignedUser,
  [REPORT_IDS.PREVENTIVE_MAINTENANCE_TASK_PERFORMANCE_BY_ASSIGNED_TEAM]:
    LookerReportEnum.WorkOrdersPreventativeMaintenanceTaskPerformanceByAssignedTeam,
  [REPORT_IDS.SERVICE_REQUEST_TENANT_SURVEY_SUMMARY_ALL]:
    LookerReportEnum.WorkOrdersServiceRequestTenantSurveySummaryAll,
  [REPORT_IDS.SERVICE_REQUEST_TENANT_SURVEY_SUMMARY_DETAILS]:
    LookerReportEnum.WorkOrdersServiceRequestTenantSurveySummaryDetails,
  [REPORT_IDS.PREVENTIVE_MAINTENANCE_TASK_PERFORMANCE_BY_SCHEDULE]:
    LookerReportEnum.WorkOrdersPreventativeMaintenanceTaskPerformanceBySchedule,
} as const;

type ReportId = keyof typeof REPORT_ID_TO_ENUM_MAP;

function mapReportIdToReportEnum(
  reportId: string
): LookerReportEnum | undefined {
  return REPORT_ID_TO_ENUM_MAP[reportId as ReportId];
}

export function mapReportToGraphQLReport(
  report: LookerReportEnum
): LookerReportType | undefined {
  return LOOKER_REPORT_MAPPING[report];
}

type EmbedURLResult = {
  reportId: string;
  embedURL: string | undefined;
  error?: Error;
};

export function useReport() {
  const [getEmbedURL] = useLazyQuery(getLookerEmbedURL);
  const [embedURLs, setEmbedURLs] = useState<EmbedURLResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  const fetchEmbedURLs = async (
    reportIds: string[],
    channel: Pick<Channel, '_id' | 'name'>,
    timeZone: string
  ) => {
    setIsLoading(true);
    setError(undefined);
    const results: EmbedURLResult[] = [];

    try {
      for (const reportId of reportIds) {
        const reportEnum = mapReportIdToReportEnum(reportId);

        if (!reportEnum) {
          throw new InvalidReportError(`Invalid report type: ${reportId}`);
        }

        const selectedReport = mapReportToGraphQLReport(reportEnum);

        if (!selectedReport) {
          throw new InvalidReportError(`Invalid report type: ${reportId}`);
        }

        const { data, error: gqlError } = await getEmbedURL({
          variables: {
            selectedReport,
            channelId: channel._id,
            channelName: channel.name,
            timezone: timeZone,
          },
        });

        if (gqlError) {
          throw gqlError;
        }

        results.push({
          reportId,
          embedURL: data?.me.lookerEmbedURL,
        });
      }

      setEmbedURLs(results);
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new GetReportEmbedURLError('Failed to get embed URLs');

      setError(error);
      setEmbedURLs([]);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchEmbedURLs,
    embedURLs,
    loading: isLoading,
    error,
  };
}
